import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import { headerBookingTable } from '../../utils/constants';
import { BookingList } from '../../components/ui/list/Bookings';
import Pagination from '../../components/atoms/Pagination';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildBookingDetailState } from '../../utils/bookingNavigation';
import { GETBookings } from '../../services/booking';
import { IBooking } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import DateFilterComp, {
  DateFilterData,
} from '../../components/ui/filters/enquiry';
import { useUrlSearchQuery } from '../../hooks/useUrlSearchQuery';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const EnquiryComp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    searchParams,
    setSearchParams,
  } = usePaginationParams();

  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [totalbooking, setTotalBookings] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [tabValue, setTabValue] = useState<string>(
    // `??` (not `||`): the "All Enquires" tab's value is an empty string,
    // which `||` would wrongly treat as "missing" and fall back from.
    () => searchParams.get('tab') ?? 'TODAYS_ENQUIRES',
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);

  const latestRequestId = useRef(0);
  const isFirstRouteLoad = useRef(true);

  useUrlSearchQuery((query) => {
    setSearchQuery(query);
    setPage(1);
  });

  const fetchBookings = useCallback(
    async (
      currentPage: number,
      limit: number,
      currentSearchQuery?: string,
      currentSort?: 'ASC' | 'DESC',
      status?: string,
      currentStartDate?: Date | null,
      currentEndDate?: Date | null,
      showLoader = true,
    ) => {
      const requestId = latestRequestId.current + 1;
      latestRequestId.current = requestId;

      try {
        if (showLoader) {
          setIsLoading(true);
        }

        const response = await GETBookings({
          page: currentPage,
          limit,
          searchQuery: currentSearchQuery,
          sortOrder: currentSort,
          status,
          startDate: currentStartDate,
          endDate: currentEndDate,
        });

        if (latestRequestId.current !== requestId) {
          return;
        }

        setBookings(response?.data || []);
        setTotalBookings(response?.count || 0);
      } catch (error) {
        if (latestRequestId.current === requestId) {
          console.error('Failed to fetch bookings:', error);
          setBookings([]);
          setTotalBookings(0);
        }
      } finally {
        if (showLoader && latestRequestId.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  const fetchCurrentBookings = useCallback(
    async (showLoader = true) => {
      await fetchBookings(
        page,
        rowsPerPage,
        searchQuery,
        sort,
        GetTabFilter(tabValue),
        filterFlag ? startDate : null,
        filterFlag ? endDate : null,
        showLoader,
      );
    },
    [
      page,
      rowsPerPage,
      searchQuery,
      sort,
      tabValue,
      filterFlag,
      startDate,
      endDate,
      fetchBookings,
    ],
  );

  useEffect(() => {
    fetchCurrentBookings(true);
  }, [fetchCurrentBookings]);

  // This works like useFocusEffect for React Web.
  // Jab details page se back aoge, location.key change hoga
  // aur list latest data ke sath refresh hogi.
  useEffect(() => {
    if (isFirstRouteLoad.current) {
      isFirstRouteLoad.current = false;
      return;
    }

    fetchCurrentBookings(true);
  }, [location.key, fetchCurrentBookings]);

  // Browser/tab focus ya back-forward cache se page visible hone par bhi refresh.
  useEffect(() => {
    const handleFocusRefresh = () => {
      fetchCurrentBookings(true);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocusRefresh();
      }
    };

    window.addEventListener('focus', handleFocusRefresh);
    window.addEventListener('pageshow', handleFocusRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocusRefresh);
      window.removeEventListener('pageshow', handleFocusRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchCurrentBookings]);

  useEffect(() => {
    if (tabValue !== 'TODAYS_ENQUIRES') {
      return;
    }

    const intervalId = setInterval(() => {
      fetchCurrentBookings(false);
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [tabValue, fetchCurrentBookings]);

  const reloadDefaultList = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterFlag(false);
    setPage(1);

    fetchBookings(
      1,
      rowsPerPage,
      searchQuery,
      sort,
      GetTabFilter(tabValue),
      null,
      null,
      true,
    );
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleOpenManageDrawer = (id: string | number) => {
    navigate(`/bookings/assign/${id}`, {
      state: buildBookingDetailState('enquiries', {
        returnSearch: location.search,
      }),
    });
  };

  const handleTabChange = (event: any, newValue: string) => {
    if (tabValue === newValue) {
      return;
    }

    setBookings([]);
    setTotalBookings(0);
    setFilterFlag(false);
    setStartDate(null);
    setEndDate(null);
    setTabValue(newValue);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', newValue);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const handleAdd = () => {
    navigate(`/bookings/create`);
  };

  const handleFilterRefresh = (Filter: DateFilterData) => {
    setPage(1);
    setFilterFlag(true);

    if (Filter.startDate) {
      setStartDate(new Date(Filter.startDate));
    } else {
      setStartDate(null);
    }

    if (Filter.endDate) {
      setEndDate(new Date(Filter.endDate));
    } else {
      setEndDate(null);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
        }}
      >
        <SmartFilter
          title="Enquires"
          createFilter={['SEARCH', 'FILTER', 'ADD']}
          filterHeader={'Filter Services'}
          addHeader={'Add Services'}
          addbuttontitle="Add Booking"
          filterElement={
            <DateFilterComp
              tabValue={tabValue}
              onResetFilter={reloadDefaultList}
              startDate={
                startDate
                  ? new Date(startDate).toISOString().split('T')[0]
                  : null
              }
              endDate={
                endDate ? new Date(endDate).toISOString().split('T')[0] : null
              }
            />
          }
          onNavigate={handleAdd}
          onSearch={handleSearchRefresh}
          onFiltered={handleFilterRefresh}
          addDrawerSize="md"
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 1,
          }}
        >
        <Box display="flex" justifyContent="center" width="100%" my={2}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={tabStyles}>
            <Tab value={'TODAYS_ENQUIRES'} label="Today Enquires" />
            <Tab value={'OLD_ENQUIRES'} label="Old Enquires" />
            <Tab value={'TECHNICIAN_ASSIGNED'} label="Technician Assigned" />
            <Tab value={'IN_PROGRESS'} label="In Progress" />
            <Tab value={'PENDING_JOBS'} label="Pending Job" />
            <Tab value={'COMPLETED'} label="Jobs Completed" />
            <Tab value={''} label="All Enquires" />
          </Tabs>
        </Box>

        <Box
          ref={scrollRef}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {bookings.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Enquiry Found
            </Typography>
          ) : (
            <BookingList
              headerCells={headerBookingTable}
              bookings={bookings}
              isLoading={isLoading}
              onManageClick={handleOpenManageDrawer}
              enquiryTab={tabValue}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalbooking}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
        </Box>
      </Box>
    </>
  );
};

export default withComponentAuth(EnquiryComp);

export const tabStyles = {
  backgroundColor: '#1B1B1B',
  borderRadius: '8px',
  padding: '6px',
  display: 'inline-flex',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    color: '#fff',
    textTransform: 'none',
    fontSize: '14px',
    padding: '10px 16px',
    minWidth: 'unset',
  },
  '& .Mui-selected': {
    color: '#fff !important',
    borderRadius: '8px',
    backgroundColor: '#d32f2f',
  },
};

const GetTabFilter = (tabValue: string) => {
  return tabValue;
};
